import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { Damage, Smartphoto, SmartphotoImage } from '@bdeo-motor/smartphoto-entities';
import { ISmartphoto } from '@bdeo-platform/private-api';
import { BdeoToastService } from '@bdeo/webcomponents';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { ApiClientService } from './api-client.service';
import { AuthService } from './auth.service';
import { CompanyService } from './company.service';
import { TransactionService } from './transaction.service';
import { DamageImagesService } from './damage-images.service';
import { environment } from '../../environments/environment';
import { orderByType } from '../entities/types/order-by.type';
import { SmartphotoStatusDetails } from '../entities/types/smartphoto-status-details.type';
import { DamageEstimationRequest, DamageEstimationResponse } from '../entities/types/damage-estimation.type';
import { IFilterQuery } from '../entities/interfaces/services/filter-query.interface';
import { StatusColor, StatusIcon, StatusLang } from '../entities/enums/status.enum';
import { Transaction } from '../entities/classes/transaction.class';
import { SmartphotoAbilities } from '../entities/classes/smartphoto-abilities.class';

type CreateResponse = { success: boolean; id: string; selfadjustLink: string };
type DamagesUpdatePayload = Omit<
  Parameters<ApiClientService['apiClient']['motor']['smartphoto']['damageSummary']['update']>[0],
  'access_token'
>;

export type DamageAction = {
  label: string;
  icon: string;
  color: string;
  callback: (damage: Damage) => void;
};

type DamageImageFile = { file: File; uuid: string };

@Injectable({
  providedIn: 'root'
})
export class SmartphotoService {
  private readonly apiClientService = inject(ApiClientService);
  private readonly authService = inject(AuthService);
  private readonly httpClient = inject(HttpClient);
  private readonly i18NextService = inject(I18NEXT_SERVICE);
  private readonly transactionService = inject(TransactionService);
  private readonly companyService = inject(CompanyService);
  private readonly smartphoto$ = new BehaviorSubject<Smartphoto | undefined>(undefined);
  private readonly actionsDamage$ = new BehaviorSubject<Damage | undefined>(undefined);
  private readonly currentSmartphotoImages$ = new BehaviorSubject<{ files: DamageImageFile[]; damage: string }[]>([]);
  private readonly damageImagesService = inject(DamageImagesService);
  public readonly abilities = new SmartphotoAbilities();
  public readonly images$ = this.smartphoto$.pipe(
    map((smartphoto) => {
      return smartphoto?.images.all.filter((image) => !['car', 'signature'].includes(image.type)) ?? [];
    })
  );
  public statusDetails$ = this.smartphoto$.pipe(
    map((smartphoto) => {
      return this.getSmartphotoStatusDetails(smartphoto);
    })
  );
  private originalSmartphoto$ = new BehaviorSubject<ISmartphoto | undefined>(undefined);

  public getOriginalSmartphoto(): ISmartphoto | undefined {
    return this.originalSmartphoto$.getValue();
  }

  public get actionsDamageObservable(): Observable<Damage | undefined> {
    return this.actionsDamage$.asObservable();
  }

  public get = {
    it: async (smartphoto$ID: string): Promise<Smartphoto> => {
      try {
        const response = await this.apiClientService.getApiClient().motor.smartphoto.get({
          selfAdjustId: smartphoto$ID,
          access_token: this.authService.getAccessToken()
        });

        const {
          data: { success, entity }
        } = response;

        if (!success) {
          throw new Error('Login call did not succeed');
        }

        const company = await this.companyService.getCompany();

        if (!company) {
          throw new Error('Company is mandatory');
        }

        const smartphoto = new Smartphoto(entity, company);
        this.originalSmartphoto$.next(entity);
        this.smartphoto$.next(smartphoto);
        return smartphoto;
      } catch (e: unknown) {
        if (e instanceof this.apiClientService.getApiClient().motor.smartphoto.get.Error) {
          this.authService.logOut();
          throw e.getActualType();
        }
        this.authService.logOut();
        throw e;
      }
    },
    /**
     * TO DO: Smartphoto[] no es el tipado correcto y debe ser cambiado por el correcto desde la API, ahora mismo este nos puede valer.
     */
    list: async (page: number, orderBy: orderByType, asc: boolean, filterQuery?: IFilterQuery): Promise<Smartphoto[]> => {
      if (filterQuery?.status?.length == 0) {
        delete filterQuery?.status;
      }

      const requestBody = {
        access_token: this.authService.getAccessToken(),
        orderBy: {
          field: orderBy,
          asc: asc
        },
        query: filterQuery,
        page: page,
        includeCaseFields: true
      };

      const requestHeaders = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      };

      const smartphotosList = await firstValueFrom(
        this.httpClient.post<{ items: Smartphoto[] }>(`${environment.baseUrl}/selfadjusts`, requestBody, requestHeaders)
      );

      return smartphotosList.items;
    },
    images: (): Observable<SmartphotoImage[]> => {
      return this.images$;
    }
  };

  public update = {
    basket: async (): Promise<void> => {
      try {
        const response = await this.apiClientService.getApiClient().motor.smartphoto.update({
          selfAdjustId: this.smartphoto$.getValue()?.id,
          access_token: this.authService.getAccessToken(),
          fields2Update: {
            basket: this.smartphoto$.getValue()?.basket?.name
          },
          action: 'update_basket',
          status: this.smartphoto$.getValue()?.status
        });

        if (!response.ok || !response.data.success) {
          throw new Error('Update call did not succeed');
        }
      } catch (e: unknown) {
        if (e instanceof this.apiClientService.getApiClient().motor.smartphoto.update.Error) {
          throw e.getActualType();
        }
        throw e;
      }
    },

    status: async (newStatus: number, fields2Update?: Record<string, unknown>): Promise<void> => {
      try {
        const response = await this.apiClientService.getApiClient().motor.smartphoto.update({
          selfAdjustId: this.smartphoto$.getValue()?.id,
          access_token: this.authService.getAccessToken(),
          fields2Update,
          status: newStatus
        });

        if (!response.ok || !response.data.success) {
          throw new Error('Update call did not succeed');
        }
      } catch (e: unknown) {
        if (e instanceof this.apiClientService.getApiClient().motor.smartphoto.update.Error) {
          throw e.getActualType();
        }
        throw e;
      }
    }
  };

  // TO DO: Mostrar toasts si ha habido un problema creando la Smartphoto o el Case
  // TO DO: Tipar el body de la llamada a create una vez que esté el endpoint en la API nueva.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async create(caseID: string, customFields: any): Promise<CreateResponse> {
    try {
      if (customFields.case) {
        delete customFields.case;
      }

      const requestBody = {
        access_token: this.authService.getAccessToken(),
        // TO DO: Recuperar idioma de la compañía y sino utilizar el del navegador
        lang: 'es-ES',
        entity: {
          case_id: caseID,
          assigned_agent_id: this.authService.getUserID(),
          ...customFields
        }
      };

      const requestHeaders = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      };

      const response = await firstValueFrom(
        this.httpClient.post<CreateResponse>(`${environment.baseUrl}/selfadjust-create`, requestBody, requestHeaders)
      );

      return response;
    } catch (error) {
      const message = this.i18NextService.t('brokers:errors.case_creation_error');
      BdeoToastService.error(message);
      throw error;
    }
  }

  public async updateDamages(payload: DamagesUpdatePayload): Promise<boolean> {
    try {
      const requestBody: DamagesUpdatePayload & { access_token: string } = {
        ...payload,
        access_token: this.authService.getAccessToken(),
        AIModulesModified: ['damageSummary']
      };

      const response = await this.apiClientService.getApiClient().motor.smartphoto.damageSummary.update(requestBody);

      if (!response.data.success || !response.ok) {
        throw new Error('Damage update call did not succeed');
      }

      return response.data.waitingForAI;
    } catch (e: unknown) {
      if (e instanceof this.apiClientService.getApiClient().motor.smartphoto.damageSummary.update.Error) {
        throw e.getActualType();
      }
      throw e;
    }
  }

  public getCurrentSmartphoto(): Observable<Smartphoto | undefined> {
    return this.smartphoto$.asObservable();
  }

  public resetSmartphoto(): void {
    const smartphoto = this.originalSmartphoto$.getValue();
    const company = this.companyService.getCurrentCompany();

    if (smartphoto && company) {
      this.smartphoto$.next(new Smartphoto(smartphoto, company));
    }
  }

  public updateSmartphoto(smartphoto: Smartphoto): void {
    this.smartphoto$.next(smartphoto);
  }

  public getSmartphotoStatusDetails(smartphoto: Smartphoto | undefined): SmartphotoStatusDetails | undefined {
    if (!smartphoto) {
      return;
    }

    const status = smartphoto.status;

    return {
      lang: StatusLang[status as keyof typeof StatusLang],
      color: StatusColor[status as keyof typeof StatusLang],
      icon: StatusIcon[status as keyof typeof StatusLang]
    };
  }

  public getSmartphotoReport(): string | undefined {
    return this.smartphoto$.getValue()?.report;
  }

  public getEvidenceCaptureLink(): string {
    const smartphoto = this.smartphoto$.getValue();
    if (!smartphoto) {
      throw new Error('Smartphoto not found.');
    }

    return `${environment.evidenceCaptureURL}/#/?secretKey=id-${smartphoto.id}&broker=true`;
  }

  public getAdditionalsLink(): string | undefined {
    const id = this.smartphoto$.getValue()?.id;

    return id ? `${environment.additionalsURL}/${id}` : undefined;
  }

  public async downloadImages(): Promise<void> {
    const zip = new JSZip();

    const images = await firstValueFrom(this.images$);
    const fileNames: string[] = [];

    const promises: Promise<void>[] = images.map(async (image) => {
      const response = await fetch(image.url, { cache: 'no-store' });
      const blob = await response.blob();
      const fileName = image.getFileName().replace(/\//g, '');
      zip.file(this.checkIfFileNameExists(fileName, fileNames), blob);
    });

    await Promise.all(promises);
    await zip.generateAsync({ type: 'blob' }).then((content) => {
      const enlace = document.createElement('a');
      enlace.href = URL.createObjectURL(content);
      enlace.download = `${this.smartphoto$.getValue()?.numRef}.zip`;
      enlace.click();
    });
  }

  private checkIfFileNameExists(fileName: string, fileNames: string[]): string {
    let index = 0;

    if (fileNames.includes(fileName)) {
      index++;
      fileName = `${fileName.split('.').slice(0, -1).join('.')}-${index}.${fileName.split('.').pop()}`;
      return this.checkIfFileNameExists(fileName, fileNames);
    } else {
      fileNames.push(fileName);
      return fileName;
    }
  }

  public downloadImage(url: string, name: string): void {
    fetch(url, { cache: 'no-store' })
      .then((response) => response.blob())
      .then((blob) => {
        saveAs(blob, name);
      });
  }

  public createUpdateTransaction(): void {
    if (!this.transactionService.empty) {
      return;
    }

    const smartphoto = this.smartphoto$.getValue();

    if (!smartphoto) {
      throw new Error('Smartphoto is not defined');
    }

    this.transactionService.add(new Transaction('smartphoto-damages-update', () => this.updateTransaction(smartphoto)));
  }

  public async getDamageEstimation(request: DamageEstimationRequest): Promise<DamageEstimationResponse> {
    try {
      const {
        data: { body }
      } = await this.apiClientService.getApiClient().motor.smartphoto.damage.price(request);

      const [damageEstimation] = body.estimations ?? [];
      if (!damageEstimation) {
        throw new Error('Damage estimation not found');
      }
      return damageEstimation;
    } catch (e) {
      BdeoToastService.error(this.i18NextService.t('brokers:errors.damage_estimation_error'));

      if (e instanceof this.apiClientService.getApiClient().motor.smartphoto.damage.price.Error) {
        throw e.getActualType();
      }

      throw e;
    }
  }

  public selectDamageForActions(damage?: Damage): void {
    this.actionsDamage$.next(damage);
  }

  public getReportName(grouping: string, isMain: boolean, index: number): string {
    if (isMain) {
      return this.i18NextService.t(`brokers:reports.main_report_${grouping}`);
    } else {
      return this.i18NextService.t(`brokers:reports.secondary_report_${grouping}`) + (index > 0 ? ` ${index + 1}` : '');
    }
  }

  public getDamageActions(damage: Damage): DamageAction[] {
    const actions: DamageAction[] = [];
    const currentSmartphoto = this.smartphoto$.getValue();

    if (!currentSmartphoto) {
      return [];
    }

    const damageOtherReport = currentSmartphoto.reports.findOtherReport().find((r) => r.damages.includes(damage));

    // Mostrar mover a daño principal
    if (damageOtherReport) {
      actions.push({
        label: this.i18NextService.t('brokers:actions.move_to') + ' ' + this.getReportName(currentSmartphoto.grouping, true, 0),
        icon: 'adjustment-filters',
        color: 'tar-grey',
        callback: () => {
          currentSmartphoto.reports.move(damage, currentSmartphoto.reports.findMainReport().uuid);
          this.actionsDamage$.next(undefined);
          currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
          this.updateSmartphoto(currentSmartphoto);
          this.createUpdateTransaction();
        }
      });
    }

    // Mostrar mover a parte secundario
    currentSmartphoto.reports.findOtherReport().forEach((report, index) => {
      if (report.uuid !== damageOtherReport?.uuid) {
        actions.push({
          label: this.i18NextService.t('brokers:actions.move_to') + ' ' + this.getReportName(currentSmartphoto.grouping, false, index),
          icon: 'adjustment-filters',
          color: 'tar-grey',
          callback: () => {
            currentSmartphoto.reports.move(damage, report.uuid);
            this.actionsDamage$.next(undefined);
            currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
            this.updateSmartphoto(currentSmartphoto);
            this.createUpdateTransaction();
          }
        });
      }
    });

    // Mostrar borrar daño
    if (!damage.deleted) {
      actions.push({
        label: 'actions.remove',
        icon: 'action-delete',
        color: 'red-velvet',
        callback: () => {
          currentSmartphoto.reports.deleteDamage(damage);
          this.actionsDamage$.next(undefined);
          currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
          this.updateSmartphoto(currentSmartphoto);
          this.createUpdateTransaction();
        }
      });
    }

    return actions;
  }

  public addImagesToCurrentSmartphoto(images: File[], damage: Damage): void {
    if (images.length === 0) {
      return;
    }

    const current = structuredClone(this.currentSmartphotoImages$.getValue());

    const damageImageFile = current.find((image) => image.damage === damage.uuid);

    if (!damageImageFile) {
      this.currentSmartphotoImages$.next([
        ...current,
        { files: images.map((image) => ({ file: image, uuid: crypto.randomUUID() })), damage: damage.uuid }
      ]);

      return;
    }

    damageImageFile.files.push(...images.map((image) => ({ file: image, uuid: crypto.randomUUID() })));

    this.currentSmartphotoImages$.next(current);
  }

  private async uploadImages(smartphoto: Smartphoto): Promise<void> {
    const images = this.currentSmartphotoImages$.getValue();

    for await (const { files, damage } of images) {
      try {
        const foundDamage = smartphoto.getMainDamages().find((d) => d.uuid === damage);

        if (!foundDamage) {
          throw new Error('Damage not found');
        }

        const imageUuids = await this.damageImagesService.uploadDamageImages(
          smartphoto,
          foundDamage,
          files.map((damageImageFile) => damageImageFile.file)
        );

        DamageImagesService.addImagesToDamage(foundDamage, imageUuids);
      } catch (error) {
        console.error(error);
        BdeoToastService.error(this.i18NextService.t('brokers:error.upload_images'));
      }
    }
  }

  private async updateTransaction(smartphoto: Smartphoto): Promise<void> {
    await this.uploadImages(smartphoto);

    const mainReport = smartphoto.reports.findMainReport();
    const otherReports = smartphoto.reports.findOtherReport();
    const deletedReport = smartphoto.reports.findDeleteReport();
    const reports = [mainReport, ...otherReports, deletedReport].map((report) => report.export());
    const damageSummary = [...smartphoto.getAllDamages(), ...smartphoto.getDeletedDamages(), ...smartphoto.concepts].map((damage) =>
      damage.export()
    );

    try {
      const waitingForAI = await this.updateDamages({
        selfadjustId: smartphoto.id,
        costEstimationInfo: smartphoto.estimationConfig,
        damageSummary,
        reports
      });

      smartphoto.waitingForAI = waitingForAI;
      this.smartphoto$.next(smartphoto);

      BdeoToastService.success(this.i18NextService.t('brokers:success_update_smartphoto'));
    } catch (e: unknown) {
      console.error(e);
      BdeoToastService.error(this.i18NextService.t('brokers:error.update_damages'));

      throw e;
    } finally {
      this.currentSmartphotoImages$.next([]);
    }
  }
}
